import { ApolloError } from '@apollo/client';
import { ReactNode, createContext, useMemo, useReducer, useContext, useRef, useCallback } from 'react';

export interface IFilterOption {
  id: string;
  name: string;
}

// Action types
export enum StudentOverviewActionType {
  SET_LOADING = 'SET_LOADING',
  SET_READY = 'SET_READY',
  SET_ERROR = 'SET_ERROR',
  SET_PARTIAL_SUCCESS = 'SET_PARTIAL_SUCCESS',
  SET_EMPTY_STATE = 'SET_EMPTY_STATE',
  RESET = 'RESET',
}

export enum StudentOverviewFilterActionType {
  SET_FACULTY = 'FILTER_SET_FACULTY',
  SET_ACADEMIC_PROGRAM = 'FILTER_SET_ACADEMIC_PROGRAM',
  SET_TYPE_OF_USER = 'FILTER_SET_TYPE_OF_USER',
  SET_INSTITUTION = 'FILTER_SET_INSTITUTION',
  SET_JOB_TITLE = 'FILTER_SET_JOB_TITLE',
  SET_SKILL = 'FILTER_SET_SKILL',
  SET_COHORT = 'FILTER_SET_COHORT',
  SET_ACCESS_CODE = 'FILTER_SET_ACCESS_CODE',
  RESET = 'FILTER_RESET',
}

// Discriminated union for status
export type StudentOverviewStatus =
  | { type: 'loading' }
  | { type: 'ready' }
  | { type: 'error'; error: ApolloError | Error }
  | { type: 'partialSuccess'; partialData: Record<string, unknown> }
  | { type: 'emptyState' };

// Action type
export type StudentOverviewAction =
  | {
      type: StudentOverviewActionType;
      payload?: {
        error?: ApolloError | Error;
        partialData?: Record<string, unknown>;
      };
    }
  | {
      type: StudentOverviewFilterActionType;
      payload?: {
        filterOption?: IFilterOption;
      };
    };

// State interface
export interface IStudentOverviewState {
  status: StudentOverviewStatus;
  isLoading: boolean;
  errors: (ApolloError | Error)[];
  lastUpdated: number | null;
  filters: IStudentOverviewFilters;
  lastFilterChanged?: 'jobTitle' | 'skill' | null;
}

export interface IStudentOverviewFilters {
  faculty?: IFilterOption;
  academicProgram?: IFilterOption;
  typeOfUser?: IFilterOption;
  institution?: IFilterOption;
  jobTitle?: IFilterOption;
  skill?: IFilterOption;
  cohort?: IFilterOption;
  accessCode?: IFilterOption;
}

// Context interface
export interface IStudentOverviewContextValue {
  state: IStudentOverviewState;
  filters: IStudentOverviewFilters;
  actions: {
    setLoading: () => void;
    setReady: () => void;
    setError: (error: ApolloError | Error) => void;
    setPartialSuccess: (partialData: Record<string, unknown>) => void;
    setEmptyState: () => void;
    reset: () => void;
    refresh: () => Promise<void>;
    setFaculty: (option: IFilterOption | undefined) => void;
    setAcademicProgram: (option: IFilterOption | undefined) => void;
    setTypeOfUser: (option: IFilterOption | undefined) => void;
    setInstitution: (option: IFilterOption | undefined) => void;
    setJobTitle: (option: IFilterOption | undefined) => void;
    setSkill: (option: IFilterOption | undefined) => void;
    setCohort: (option: IFilterOption | undefined) => void;
    setAccessCode: (option: IFilterOption | undefined) => void;
    resetFilters: () => void;
  };
}

const initialFilters: IStudentOverviewFilters = {
  faculty: undefined,
  academicProgram: undefined,
  typeOfUser: undefined,
  institution: undefined,
  jobTitle: undefined,
  skill: undefined,
  cohort: undefined,
  accessCode: undefined,
};

// Initial state
const initialState: IStudentOverviewState = {
  status: { type: 'loading' },
  isLoading: true,
  errors: [],
  lastUpdated: null,
  filters: initialFilters,
  lastFilterChanged: null,
};

// Reducer
export const studentOverviewReducer = (
  state: IStudentOverviewState,
  action: StudentOverviewAction,
): IStudentOverviewState => {
  const baseUpdate = { lastUpdated: Date.now() };

  switch (action.type) {
    case StudentOverviewActionType.SET_LOADING:
      return {
        ...state,
        status: { type: 'loading' },
        isLoading: true,
        errors: [],
      };

    case StudentOverviewActionType.SET_READY:
      return {
        ...state,
        status: { type: 'ready' },
        isLoading: false,
        errors: [],
        ...baseUpdate,
      };

    case StudentOverviewActionType.SET_ERROR:
      return {
        ...state,
        status: { type: 'error', error: action.payload?.error || new Error('Unknown error') },
        isLoading: false,
        errors: [action.payload?.error || new Error('Unknown error')],
        ...baseUpdate,
      };

    case StudentOverviewActionType.SET_PARTIAL_SUCCESS:
      return {
        ...state,
        status: { type: 'partialSuccess', partialData: action.payload?.partialData || {} },
        isLoading: false,
        ...baseUpdate,
      };

    case StudentOverviewActionType.SET_EMPTY_STATE:
      return {
        ...state,
        status: { type: 'emptyState' },
        isLoading: false,
        errors: [],
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_FACULTY:
      return {
        ...state,
        filters: {
          ...state.filters,
          faculty: action.payload?.filterOption,
          academicProgram: undefined,
          jobTitle: undefined,
          skill: undefined,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_ACADEMIC_PROGRAM:
      return {
        ...state,
        filters: {
          ...state.filters,
          academicProgram: action.payload?.filterOption,
          jobTitle: undefined,
          skill: undefined,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_TYPE_OF_USER:
      return {
        ...state,
        filters: {
          ...state.filters,
          typeOfUser: action.payload?.filterOption,
          jobTitle: undefined,
          skill: undefined,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_INSTITUTION:
      return {
        ...state,
        filters: {
          ...state.filters,
          institution: action.payload?.filterOption,
          faculty: undefined,
          academicProgram: undefined,
          jobTitle: undefined,
          skill: undefined,
          cohort: undefined,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_JOB_TITLE:
      return {
        ...state,
        filters: {
          ...state.filters,
          jobTitle: action.payload?.filterOption,
          skill: undefined,
        },
        lastFilterChanged: 'jobTitle',
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_SKILL:
      return {
        ...state,
        filters: {
          ...state.filters,
          jobTitle: undefined,
          skill: action.payload?.filterOption,
        },
        lastFilterChanged: 'skill',
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_COHORT:
      return {
        ...state,
        filters: {
          ...state.filters,
          cohort: action.payload?.filterOption,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.SET_ACCESS_CODE:
      return {
        ...state,
        filters: {
          ...state.filters,
          accessCode: action.payload?.filterOption,
        },
        lastFilterChanged: null,
        ...baseUpdate,
      };

    case StudentOverviewFilterActionType.RESET:
      return {
        ...state,
        filters: initialFilters,
        ...baseUpdate,
      };

    case StudentOverviewActionType.RESET:
      return initialState;

    default:
      return state;
  }
};

// Context
const StudentOverviewContext = createContext<IStudentOverviewContextValue | undefined>(undefined);

// Provider props
export interface IStudentOverviewProviderProps {
  children: ReactNode;
  onRefresh?: () => Promise<void> | void;
}

// Provider component
export const StudentOverviewProvider = ({ children, onRefresh }: IStudentOverviewProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(studentOverviewReducer, initialState);
  const isRefreshingRef = useRef(false);

  const setFaculty = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_FACULTY,
      payload: { filterOption: option },
    });
  }, []);

  const setAcademicProgram = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_ACADEMIC_PROGRAM,
      payload: { filterOption: option },
    });
  }, []);

  const setTypeOfUser = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_TYPE_OF_USER,
      payload: { filterOption: option },
    });
  }, []);

  const setInstitution = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_INSTITUTION,
      payload: { filterOption: option },
    });
  }, []);

  const setJobTitle = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_JOB_TITLE,
      payload: { filterOption: option },
    });
  }, []);

  const setSkill = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_SKILL,
      payload: { filterOption: option },
    });
  }, []);

  const setCohort = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_COHORT,
      payload: { filterOption: option },
    });
  }, []);

  const setAccessCode = useCallback((option: IFilterOption | undefined) => {
    dispatch({
      type: StudentOverviewFilterActionType.SET_ACCESS_CODE,
      payload: { filterOption: option },
    });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: StudentOverviewFilterActionType.RESET });
  }, []);

  const actions = useMemo(
    () => ({
      setLoading: () => dispatch({ type: StudentOverviewActionType.SET_LOADING }),
      setReady: () => dispatch({ type: StudentOverviewActionType.SET_READY }),
      setError: (error: ApolloError | Error) =>
        dispatch({
          type: StudentOverviewActionType.SET_ERROR,
          payload: { error },
        }),
      setPartialSuccess: (partialData: Record<string, unknown>) =>
        dispatch({
          type: StudentOverviewActionType.SET_PARTIAL_SUCCESS,
          payload: { partialData },
        }),
      setEmptyState: () => dispatch({ type: StudentOverviewActionType.SET_EMPTY_STATE }),
      reset: () => dispatch({ type: StudentOverviewActionType.RESET }),
      refresh: async () => {
        if (onRefresh && !isRefreshingRef.current) {
          isRefreshingRef.current = true;
          dispatch({ type: StudentOverviewActionType.SET_LOADING });
          try {
            await onRefresh();
            dispatch({ type: StudentOverviewActionType.SET_READY });
          } catch (error) {
            dispatch({
              type: StudentOverviewActionType.SET_ERROR,
              payload: { error: error as ApolloError | Error },
            });
          } finally {
            isRefreshingRef.current = false;
          }
        }
      },
      setFaculty,
      setAcademicProgram,
      setTypeOfUser,
      setInstitution,
      setJobTitle,
      setSkill,
      setCohort,
      setAccessCode,
      resetFilters,
    }),
    [
      onRefresh,
      setFaculty,
      setAcademicProgram,
      setTypeOfUser,
      setInstitution,
      setJobTitle,
      setSkill,
      setCohort,
      setAccessCode,
      resetFilters,
    ],
  );

  const filters = useMemo(() => state.filters, [state.filters]);

  const contextValue = useMemo(() => ({ state, actions, filters }), [state, actions, filters]);

  return <StudentOverviewContext.Provider value={contextValue}>{children}</StudentOverviewContext.Provider>;
};

// Hook to use the context
export const useStudentOverviewContext = (): IStudentOverviewContextValue => {
  const context = useContext(StudentOverviewContext);

  if (context === undefined) {
    throw new Error('useStudentOverviewContext must be used within a StudentOverviewProvider');
  }

  return context;
};

export default StudentOverviewProvider;
